import {Card, CardContent, CardHeader, CardTitle} from '@common-web/components/ui/card';
import {Button} from '@common-web/components/ui/button';
import {Input} from '@common-web/components/ui/input';
import {Label} from '@common-web/components/ui/label';
import {GenderSelect, MEMBER_GENDER_CODES} from '@client/components/GenderSelect';
import {StateSelect} from '@client/components/StateSelect';
import type {MemberFieldDef, MemberInfo, AddressInfo} from './types';
import {MEMBER_ONLY_FIELDS, ADDRESS_ONLY_FIELDS, getLabelForKey} from './types';

type MemberInfoStepProps = {
  member: MemberInfo;
  onUpdateMember: <K extends keyof MemberInfo>(field: K, value: MemberInfo[K]) => void;
  address: AddressInfo;
  onUpdateAddress: <K extends keyof AddressInfo>(field: K, value: AddressInfo[K]) => void;
  canProceed: boolean;
  onContinue: () => void;
};

export function MemberInfoStep({
  member,
  onUpdateMember,
  address,
  onUpdateAddress,
  canProceed,
  onContinue,
}: MemberInfoStepProps) {
  return (
    <>
      <Card className='border-0 shadow-lg bg-white overflow-hidden mb-6'>
        <CardHeader className='bg-gradient-to-r from-gray-50 to-white border-b border-gray-100 px-6 py-4'>
          <CardTitle className='text-lg font-semibold text-brand-navy'>
            Step 1: Member Info
          </CardTitle>
        </CardHeader>
        <CardContent className='p-6'>
          <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
            {MEMBER_ONLY_FIELDS.map((field: MemberFieldDef) => {
              const suffix = field.required ? ' *' : '';

              if (field.key === 'sexCode') {
                return (
                  <div key={field.key} className='space-y-2'>
                    <Label htmlFor={field.key}>
                      {field.label}
                      {suffix}
                    </Label>
                    <GenderSelect
                      codes={MEMBER_GENDER_CODES}
                      id={field.key}
                      value={member.sexCode}
                      onValueChange={(sexCode) => onUpdateMember('sexCode', sexCode)}
                    />
                  </div>
                );
              }

              const key = field.key as Exclude<keyof MemberInfo, 'sexCode'>;
              return (
                <div key={field.key} className='space-y-2'>
                  <Label htmlFor={field.key}>
                    {field.label}
                    {suffix}
                  </Label>
                  <Input
                    id={field.key}
                    type={field.inputType === 'date' ? 'date' : (field.inputType ?? 'text')}
                    value={member[key]}
                    onChange={(e) => onUpdateMember(key, e.target.value)}
                    placeholder={field.key === 'ssn' ? '123-45-6789' : undefined}
                  />
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <Card className='border-0 shadow-lg bg-white overflow-hidden mb-6'>
        <CardHeader className='bg-gradient-to-r from-gray-50 to-white border-b border-gray-100 px-6 py-4'>
          <CardTitle className='text-lg font-semibold text-brand-navy'>Address</CardTitle>
        </CardHeader>
        <CardContent className='p-6'>
          <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
            {ADDRESS_ONLY_FIELDS.map((field) => {
              const key = field.key as keyof AddressInfo;
              const isFullWidth = key === 'street' || key === 'street2';

              if (key === 'state') {
                return (
                  <div key={field.key} className='space-y-2'>
                    <Label htmlFor={field.key}>{field.label}</Label>
                    <StateSelect
                      value={address.state}
                      onValueChange={(v) => onUpdateAddress('state', v)}
                    />
                  </div>
                );
              }

              return (
                <div key={field.key} className={`space-y-2${isFullWidth ? ' md:col-span-2' : ''}`}>
                  <Label htmlFor={field.key}>{field.label}</Label>
                  <Input
                    id={field.key}
                    value={address[key]}
                    onChange={(e) => onUpdateAddress(key, e.target.value)}
                  />
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <div className='flex items-center justify-end'>
        <Button onClick={onContinue} disabled={!canProceed}>
          Continue to Step 2
        </Button>
      </div>
    </>
  );
}
